/**
 * 4D Simülasyon Sayfası - Minimax Video-01
 * Bebeği gerçekten canlandırır: göz kırpma, gülümseme, baş hareketi
 */

import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  Play,
  Pause,
  Upload,
  Video,
  Loader2,
  ArrowLeft,
  Download,
  RefreshCw,
  Sparkles,
  Clapperboard,
  Eye,
  Smile,
  Moon,
  Search,
  AlertCircle,
  Heart,
  MessageCircle,
  QrCode,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { ApiError } from '../../lib/api';
import { InsufficientCreditsModal, isInsufficientCreditsError } from '../../components/credits/InsufficientCreditsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Backend'in 4 LivePortrait progress mesajına eşlenmiş aşamalar.
// (Bkz. backend/generation/routes.py:797)
const STAGES = [
  { key: 'starting',   label: 'Hazırlık',           matches: ['başlat', 'hazırlan'] },
  { key: 'face',       label: 'Yüz haritalama',     matches: ['yüz haritala', 'face'] },
  { key: 'transfer',   label: 'İfade aktarma',      matches: ['ifade'] },
  { key: 'render',     label: 'Video birleştirme',  matches: ['birleştir', 'rötuş', 'render'] },
];

const ProcessingStages = ({ progress }) => {
  const lower = (progress || '').toLowerCase();
  let currentIdx = 0;
  for (let i = STAGES.length - 1; i >= 0; i--) {
    if (STAGES[i].matches.some(m => lower.includes(m))) { currentIdx = i; break; }
  }
  return (
    <div className="w-full px-6 py-8 space-y-5" data-testid="processing-stages">
      <div className="flex items-center justify-center mb-2">
        <div className="relative w-14 h-14">
          <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
            <circle cx="28" cy="28" r="24" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
            <circle
              cx="28" cy="28" r="24" fill="none"
              stroke="url(#lpgrad)" strokeWidth="3" strokeLinecap="round"
              strokeDasharray={`${((currentIdx + 1) / STAGES.length) * 150.8} 150.8`}
              className="transition-all duration-700"
            />
            <defs>
              <linearGradient id="lpgrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#22d3ee" />
                <stop offset="100%" stopColor="#a855f7" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[10px] font-medium text-white/60">{currentIdx + 1}/{STAGES.length}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2.5 max-w-[280px] mx-auto">
        {STAGES.map((s, i) => {
          const done = i < currentIdx;
          const active = i === currentIdx;
          return (
            <div key={s.key} className="flex items-center gap-3" data-testid={`stage-${s.key}`}>
              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                done ? 'bg-emerald-500/20 text-emerald-300' :
                active ? 'bg-cyan-500/20 text-cyan-300' : 'bg-white/[0.04] text-white/20'
              }`}>
                {done ? (
                  <svg className="w-3 h-3" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : active ? (
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                ) : (
                  <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
                )}
              </div>
              <span className={`text-xs transition-colors ${
                done ? 'text-white/40' : active ? 'text-white/90 font-medium' : 'text-white/30'
              }`}>{s.label}</span>
              {active && (
                <span className="ml-auto text-[10px] text-white/40">çalışıyor…</span>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-white/30 text-center pt-1">
        Yüksek kalite üretim — yaklaşık 3 dakika
      </p>
    </div>
  );
};

// Eski çalışan Minimax stilleri geri getirildi (Mayıs 2026, kullanıcı talebi).
// "natural" artık Kling AI'a yönlendiriliyor — chrome auto-crop + ~3 dk.
const ANIMATION_STYLES = [
  { id: 'natural', label: 'Yumuşak Doğal', desc: 'Hafif göz kırpma, doğal gülümseme', icon: Heart, color: 'from-emerald-500 to-teal-400', provider: 'kling', badge: 'Kling AI' },
  { id: 'gentle', label: 'Nazik', desc: 'Yumuşak göz kırpma, hafif gülümseme', icon: Smile, color: 'from-pink-500 to-rose-400', provider: 'minimax', badge: 'Minimax' },
  { id: 'playful', label: 'Oyuncu', desc: 'Neşeli bakış, mutlu ifadeler', icon: Sparkles, color: 'from-amber-500 to-orange-400', provider: 'minimax', badge: 'Minimax' },
  { id: 'curious', label: 'Meraklı', desc: 'Etrafına bakan, keşfeden bakış', icon: Search, color: 'from-cyan-500 to-blue-400', provider: 'minimax', badge: 'Minimax' },
  { id: 'sleepy', label: 'Uykulu', desc: 'Huzurlu, uyuklayan, esneme', icon: Moon, color: 'from-purple-500 to-indigo-400', provider: 'minimax', badge: 'Minimax' },
];

const Simulation4DPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [sourceImage, setSourceImage] = useState(null);
  const [sourceType, setSourceType] = useState(searchParams.get('type') || 'baby_face');
  const [animationStyle, setAnimationStyle] = useState('natural');

  const [status, setStatus] = useState('idle'); // idle | processing | completed | failed
  const [progress, setProgress] = useState('');
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // Step 8: 402 INSUFFICIENT_CREDITS modal state
  const [insufficientCredits, setInsufficientCredits] = useState(null);

  const videoRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('neobreed_token');

  useEffect(() => {
    const storedImage = sessionStorage.getItem('4d_source_image');
    if (storedImage) {
      setSourceImage(storedImage);
      sessionStorage.removeItem('4d_source_image');
    }
  }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSourceImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleStart = async () => {
    if (!sourceImage) {
      toast.error('Lütfen bir görüntü seçin');
      return;
    }

    setStatus('processing');
    setProgress('Başlatılıyor...');
    setError(null);
    setVideoUrl(null);

    try {
      const res = await fetch(`${API_URL}/api/generation/4d-simulation/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          image_base64: sourceImage,
          source_type: sourceType,
          animation_style: animationStyle,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        // Step 8: HTTP 402 INSUFFICIENT_CREDITS — open the credits modal
        // instead of just toast'ing. Backend returns structured detail.
        if (res.status === 402) {
          const detail = (data && typeof data.detail === 'object') ? data.detail : null;
          const apiErr = new ApiError(detail?.message_tr || 'Yetersiz kredi', 402, detail);
          if (isInsufficientCreditsError(apiErr)) {
            setInsufficientCredits(apiErr.detail);
            setStatus('idle');
            return;
          }
        }
        throw new Error(data.detail || 'Başlatma hatası');
      }

      startPolling(data.job_id);
      toast.info('Video oluşturuluyor, yaklaşık 2 dakika sürecek...');
    } catch (err) {
      setStatus('failed');
      setError(err.message);
      toast.error(err.message);
    }
  };

  const startPolling = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/generation/4d-simulation/status/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setProgress(data.progress || '');

        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          setStatus('completed');
          setVideoUrl(data.video_url);
          toast.success('4D Video hazır!');
        } else if (data.status === 'failed') {
          clearInterval(pollRef.current);
          setStatus('failed');
          setError(data.error || 'Bilinmeyen hata');
          toast.error('Video oluşturulamadı');
        }
      } catch { /* ignore */ }
    }, 4000);
  };

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const togglePlayPause = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) { videoRef.current.play(); setIsPlaying(true); }
    else { videoRef.current.pause(); setIsPlaying(false); }
  };

  const handleDownload = () => {
    if (!videoUrl) return;
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = `4d-bebek-${animationStyle}-${Date.now()}.mp4`;
    a.target = '_blank';
    a.click();
  };

  const selectedStyle = ANIMATION_STYLES.find(s => s.id === animationStyle);
  const currentProvider = selectedStyle?.provider || 'minimax';
  const currentBadge = selectedStyle?.badge || 'Minimax';

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <header className="border-b border-white/5 bg-[#0a0a0f]/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-white/60 hover:text-white" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
          </Button>
          <div className="h-5 w-px bg-white/10" />
          <Clapperboard className="w-5 h-5 text-cyan-400" />
          <h1 className="text-lg font-semibold text-white">4D Simülasyon</h1>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${
            currentProvider === 'liveportrait' 
              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
              : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
          }`}>
            {currentBadge}
          </span>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left - Source & Controls */}
          <div className="space-y-5">
            <Card className="bg-[#12121a] border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-white/90 text-base flex items-center gap-2">
                  <Upload className="w-4 h-4 text-cyan-400" />
                  Kaynak Görüntü
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sourceImage ? (
                  <div className="relative group">
                    <img
                      src={sourceImage.startsWith('data:') || sourceImage.startsWith('http') ? sourceImage : `${API_URL}${sourceImage}`}
                      alt="Kaynak"
                      className="w-full rounded-lg object-contain max-h-[400px]"
                      style={{ imageRendering: 'auto' }}
                      data-testid="source-image-preview"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <Button size="sm" variant="secondary" onClick={() => fileInputRef.current?.click()} data-testid="change-image-button">Değiştir</Button>
                    </div>
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                        {sourceType === 'ultrasound_4k' ? 'Ultrason 4K' : 'AI Baby Face'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <label className="block cursor-pointer" data-testid="upload-area">
                    <div className="border-2 border-dashed border-white/10 rounded-xl p-12 text-center hover:border-cyan-500/30 transition-colors">
                      <Upload className="w-10 h-10 text-white/20 mx-auto mb-3" />
                      <p className="text-sm text-white/40">Görüntü yükle</p>
                      <p className="text-xs text-white/20 mt-1">AI Baby Face veya Ultrason 4K çıktısı</p>
                    </div>
                  </label>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </CardContent>
            </Card>

            <Card className="bg-[#12121a] border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-white/90 text-base flex items-center gap-2">
                  <Eye className="w-4 h-4 text-purple-400" />
                  Animasyon Stili
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Featured: Yumuşak Doğal - LivePortrait */}
                  {ANIMATION_STYLES.filter(s => s.provider === 'liveportrait').map((style) => {
                    const Icon = style.icon;
                    const isActive = animationStyle === style.id;
                    return (
                      <button
                        key={style.id}
                        onClick={() => setAnimationStyle(style.id)}
                        disabled={status === 'processing'}
                        className={`relative w-full p-4 rounded-xl text-left transition-all border ${
                          isActive
                            ? 'border-emerald-500/50 bg-emerald-500/10 ring-1 ring-emerald-500/30'
                            : 'border-white/5 bg-white/[0.02] hover:border-emerald-500/20 hover:bg-white/[0.04]'
                        } ${status === 'processing' ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid={`style-${style.id}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${style.color} flex items-center justify-center`}>
                            <Icon className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-white/80'}`}>{style.label}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">LivePortrait</span>
                            </div>
                            <p className="text-xs text-white/40 mt-0.5">{style.desc}</p>
                          </div>
                          {isActive && <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <Button
                  className="w-full mt-5 bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white font-semibold py-3"
                  size="lg"
                  onClick={handleStart}
                  disabled={!sourceImage || status === 'processing'}
                  data-testid="start-4d-button"
                >
                  {status === 'processing' ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Video oluşturuluyor...</>
                  ) : (
                    <><Sparkles className="w-5 h-5 mr-2" />Bebeği Canlandır</>
                  )}
                </Button>

                {status === 'processing' && (
                  <p className="text-xs text-white/30 text-center mt-2">
                    Kling AI ile yüksek kalite — yaklaşık 3 dakika sürer
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right - Video Output */}
          <div className="space-y-5">
            <Card className="bg-[#12121a] border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-white/90 text-base flex items-center gap-2">
                  <Video className="w-4 h-4 text-emerald-400" />
                  4D Video
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="aspect-video rounded-xl overflow-hidden bg-black/60 border border-white/5 flex items-center justify-center" data-testid="video-output-area">
                  {status === 'processing' ? (
                    <ProcessingStages progress={progress} />
                  ) : status === 'completed' && videoUrl ? (
                    <div className="w-full h-full relative">
                      <video
                        ref={videoRef}
                        src={videoUrl}
                        className="w-full h-full object-contain"
                        loop
                        playsInline
                        autoPlay
                        onPlay={() => setIsPlaying(true)}
                        onPause={() => setIsPlaying(false)}
                        data-testid="video-player"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                        <div className="flex items-center justify-center">
                          <Button size="icon" variant="ghost" onClick={togglePlayPause} className="text-white hover:bg-white/20 rounded-full w-10 h-10" data-testid="play-pause-button">
                            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : status === 'failed' ? (
                    <div className="text-center space-y-3 p-8">
                      <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
                      <p className="text-sm text-red-300">{error || 'Bir hata oluştu'}</p>
                      <Button size="sm" variant="outline" onClick={handleStart} className="border-white/10 text-white/70 hover:text-white" data-testid="retry-button">
                        <RefreshCw className="w-4 h-4 mr-1" /> Tekrar Dene
                      </Button>
                    </div>
                  ) : (
                    <div className="text-center space-y-3 p-8">
                      <Clapperboard className="w-12 h-12 text-white/10 mx-auto" />
                      <p className="text-sm text-white/30">4D Video burada görünecek</p>
                      <p className="text-xs text-white/15">LivePortrait ile bebeğiniz gerçekçi şekilde canlanacak</p>
                    </div>
                  )}
                </div>

                {status === 'completed' && videoUrl && (
                  <div className="space-y-3 mt-4">
                    <div className="flex gap-3">
                      <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={handleDownload} data-testid="download-video-button">
                        <Download className="w-4 h-4 mr-2" /> Videoyu İndir
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 border-white/10 text-white/70 hover:text-white"
                        onClick={() => { setStatus('idle'); setVideoUrl(null); }}
                        data-testid="new-simulation-button"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" /> Farklı Stil Dene
                    </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={async () => {
                          // If we still hold a (temporary) replicate URL —
                          // either because this video pre-dated the auto-
                          // archive flow OR because archival failed
                          // server-side — re-archive on demand so the link
                          // sent over WhatsApp keeps working past 24h.
                          let shareableUrl = videoUrl;
                          try {
                            if (shareableUrl && shareableUrl.includes('replicate.delivery')) {
                              const { fetchWithAuth } = await import('../../lib/api');
                              const res = await fetchWithAuth('/share/video', {
                                method: 'POST',
                                body: JSON.stringify({ video_url: shareableUrl }),
                              });
                              if (res?.token) {
                                shareableUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/video/${res.token}`;
                              }
                            }
                          } catch (e) {
                            // Non-fatal — fall back to the original URL.
                            console.warn('On-demand video archive failed', e);
                          }
                          const text = encodeURIComponent(`NeoBreed AI - 4D Bebek Simulasyonu hazir!\n\nVideoyu izlemek icin:\n${shareableUrl}`);
                          window.open(`https://wa.me/?text=${text}`, '_blank');
                        }}
                        className="flex-1 bg-[#25D366] hover:bg-[#1da851] text-white"
                        size="sm"
                        data-testid="share-video-whatsapp"
                      >
                        <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
                      </Button>
                      <Button
                        onClick={handleDownload}
                        variant="outline"
                        className="flex-1 border-white/10 text-white/70 hover:text-white"
                        size="sm"
                        data-testid="share-video-download"
                      >
                        <Download className="w-4 h-4 mr-1.5" /> Indir
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <InsufficientCreditsModal
        open={!!insufficientCredits}
        detail={insufficientCredits}
        onClose={() => setInsufficientCredits(null)}
        onResolved={() => {
          setInsufficientCredits(null);
          toast.success('Kredi yüklendi. Üretimi tekrar başlatabilirsiniz.');
        }}
      />
    </div>
  );
};

export default Simulation4DPage;
